import { FC, memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DATE_TIME_FORMATS,
  WORK_ORDER_HOVER_STATUS_MAP,
  WORK_ORDER_STATUSES_LABELS,
  WORK_ORDER_SUBSTATUSES_MAP,
} from "@miplatform/constants";
import { IWorkOrder, WorkOrderStatus } from "@miplatform/types";
import {
  formatDate,
  getWorkOrderSubstatus,
  isDateAfter,
} from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

interface WorkOrderCardProps {
  workOrder: IWorkOrder;
}

export const WorkOrderCard: FC<WorkOrderCardProps> = memo(({ workOrder }) => {
  const substatus = getWorkOrderSubstatus(workOrder);
  const { templateName = "--", status, hardDueDate, completeDate } = workOrder;
  const { classes } = useStyles({
    status,
    substatus,
    hardDueDate,
    completeDate,
  });
  const { label } =
    WORK_ORDER_SUBSTATUSES_MAP[substatus] || WORK_ORDER_SUBSTATUSES_MAP.default;
  const date = useMemo(() => {
    if (completeDate) {
      return `Completed: ${formatDate(
        completeDate,
        DATE_TIME_FORMATS.FORMAT_MM_DD_YY
      )}`;
    }

    if (hardDueDate) {
      return `Due: ${formatDate(
        hardDueDate,
        DATE_TIME_FORMATS.FORMAT_MM_DD_YY
      )}`;
    }

    return "--";
  }, [completeDate, hardDueDate]);

  return (
    <Box className={classes.root}>
      <Typography className={classes.template}>{templateName}</Typography>
      <Typography className={classes.date}>{date}</Typography>
      <Box className={classes.status}>
        {label || WORK_ORDER_STATUSES_LABELS[status]}
      </Box>
    </Box>
  );
});

const useStyles = makeStyles<{
  completeDate?: string;
  hardDueDate?: string;
  substatus: string;
  status: WorkOrderStatus;
}>()((theme, { completeDate, hardDueDate, status, substatus }) => {
  const { background, text, main } = WORK_ORDER_HOVER_STATUS_MAP[status] as {
    background: string;
    text: string;
    main: string;
  };
  const {
    background: substatusBackground,
    border: substatusBorder,
    status: substatusStyles,
  } = WORK_ORDER_SUBSTATUSES_MAP[substatus] ||
  WORK_ORDER_SUBSTATUSES_MAP.default;

  return {
    root: {
      display: "flex",
      backgroundColor: substatusBackground,
      border: `0.5px solid ${substatusBorder}`,
      padding: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      gap: theme.spacing(2),
    },
    template: {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.semiBold,
    },
    date: {
      fontSize: theme.fontSize.small,
      color: theme.palette.grey.medium,
      flex: 1,
      ...(completeDate &&
        hardDueDate &&
        isDateAfter(completeDate, hardDueDate) && {
          color: theme.palette.error.main,
        }),
    },
    status: {
      backgroundColor: substatusStyles?.background || background,
      border: `0.5px solid ${substatusStyles?.border || main}`,
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(0.5, 1),
      fontSize: theme.fontSize.small,
      color: substatusStyles?.text || text || main,
    },
  };
});
